import React, { useMemo } from "react";
import { Box, TextField, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import { DeliverableRow } from "../../RFCPricing/components/BillingMilestones";
import { renderCurrencyIconColored } from "../../../utils/CurrencyUtils";
import {
  AMOUNT_REGEX,
  DELIVERABLE_STATUS,
  formatAmountWithCommas,
  isInitialDeposit,
  isoDay,
  milestoneAmountOf,
  milestoneDueDateOf,
  milestoneLabelOf,
  nextLocalId,
} from "./helpers";

export default function BillingMilestoneTable({
  milestones,
  editedById,
  setEditedById,
  selectedMilestoneIds,
  onToggleMilestone,
  onAutoSelectMilestone,
  syntheticEdit,
  setSyntheticEdit,
  crPrice,
  readOnly,
}) {
  const selectable = useMemo(
    () => (milestones || []).filter((m) => !isInitialDeposit(m)),
    [milestones],
  );

  const updateRow = (id, patchOrFn) =>
    setEditedById((prev) => {
      const cur = prev[id] || {};
      const patch =
        typeof patchOrFn === "function" ? patchOrFn(cur) : patchOrFn;
      return {
        ...prev,
        [id]: { ...cur, ...patch },
      };
    });

  const gridCols = "36px 1.6fr 150px 130px 32px";

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
      {/* Header — desktop only */}
      <Box
        sx={{
          display: { xs: "none", md: "grid" },
          gridTemplateColumns: gridCols,
          gap: 2,
          padding: "4px 14px",
          fontSize: 12,
          color: "#6B7280",
          fontFamily: "Inter Tight",
          fontWeight: 500,
          alignItems: "center",
        }}
      >
        <Box />
        <Box>Milestone</Box>
        <Box>Due Date</Box>
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            justifyContent: "flex-end",
            justifySelf: "end",
          }}
        >
          Amount
          <Box
            component="span"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              color: "#6B7280",
            }}
          >
            ({renderCurrencyIconColored("black-xs-small")})
          </Box>
        </Box>
        <Box />
      </Box>

      <ChangeRequestMilestoneRow
        edit={syntheticEdit}
        onUpdate={(patchOrFn) =>
          setSyntheticEdit((prev) => {
            const cur = prev || {};
            const patch =
              typeof patchOrFn === "function" ? patchOrFn(cur) : patchOrFn;
            return { ...cur, ...patch };
          })
        }
        crPrice={crPrice}
        readOnly={readOnly}
        gridCols={gridCols}
      />

      {selectable.map((m) => (
        <MilestoneEditRow
          key={m.id}
          milestone={m}
          edit={editedById[String(m.id)]}
          onUpdate={(patch) => updateRow(String(m.id), patch)}
          readOnly={readOnly}
          selected={selectedMilestoneIds?.has(String(m.id))}
          onToggleRow={() => onToggleMilestone?.(String(m.id))}
          onAutoSelect={() => onAutoSelectMilestone?.(String(m.id))}
          gridCols={gridCols}
        />
      ))}
    </Box>
  );
}

// Compact MUI outlined-input styling used by both row variants.
const compactOutlinedSx = (changed) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    fontFamily: "Inter Tight",
    fontSize: 13,
    "& fieldset": {
      borderColor: changed ? "#4D6BF6" : "#D7D8D9",
      borderWidth: changed ? "1.5px" : "1px",
    },
    "&:hover fieldset": {
      borderColor: changed ? "#4D6BF6" : "#9CA3AF",
    },
    "&.Mui-focused fieldset": { borderColor: "#4D6BF6" },
  },
  "& .MuiOutlinedInput-input": { padding: "6px 8px", fontSize: 13 },
});

// Pinned synthetic row representing the new CR milestone. Locked-on
// checkbox, editable amount + due date, and its own deliverables
// accordion.
function ChangeRequestMilestoneRow({
  edit,
  onUpdate,
  crPrice,
  readOnly,
  gridCols,
}) {
  const safeEdit = edit || {};
  const expanded = !!safeEdit.expanded;
  const setExpanded = (next) =>
    onUpdate({
      expanded: typeof next === "function" ? next(expanded) : next,
    });

  const baseAmount = String(crPrice ?? "");
  const amountChanged = String(safeEdit.amount ?? "") !== baseAmount;

  // Always send a functional updater up so the latest synthetic
  // state powers `removeDeliverable` / `removeSubActivity` (no stale
  // closure on `safeEdit.deliverables`).
  const handleDeliverablesUpdate = (updater) =>
    onUpdate((prev) => ({
      deliverables:
        typeof updater === "function"
          ? updater(prev?.deliverables || [])
          : updater,
    }));

  return (
    <Box
      sx={{
        border: "1px solid #E5E7EB",
        borderRadius: "12px",
        background: "#F8FAFF",
        overflow: "hidden",
      }}
    >
      {/* Desktop layout — single-row grid. */}
      <Box
        sx={{
          display: { xs: "none", md: "grid" },
          gridTemplateColumns: gridCols,
          gap: 2,
          alignItems: "center",
          padding: "10px 14px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box
            sx={{
              width: 18,
              height: 18,
              borderRadius: "4px",
              border: "1.5px solid #4D6BF6",
              backgroundColor: "#4D6BF6",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "none",
            }}
          >
            <CheckIcon sx={{ fontSize: 14, color: "#fff" }} />
          </Box>
        </Box>

        <TextField
          placeholder="Change Request Milestone"
          value={safeEdit.name ?? ""}
          onChange={(e) => {
            if (readOnly) return;
            onUpdate({ name: e.target.value });
          }}
          inputProps={{
            readOnly,
            style: {
              cursor: readOnly ? "not-allowed" : "text",
              fontWeight: 600,
            },
          }}
          sx={{
            ...compactOutlinedSx(false),
            width: "45%",
          }}
        />

        <TextField
          type="date"
          value={isoDay(safeEdit.dueDate)}
          onChange={(e) => {
            if (readOnly) return;
            onUpdate({ dueDate: e.target.value });
          }}
          inputProps={{
            readOnly,
            style: { cursor: readOnly ? "not-allowed" : "text" },
          }}
          sx={compactOutlinedSx(!!safeEdit.dueDate)}
        />

        <TextField
          placeholder="Amount"
          value={formatAmountWithCommas(safeEdit.amount ?? "")}
          onChange={(e) => {
            if (readOnly) return;
            const stripped = e.target.value.replace(/,/g, "");
            if (
              stripped === "" ||
              stripped === "-" ||
              AMOUNT_REGEX.test(stripped)
            ) {
              onUpdate({ amount: stripped });
            }
          }}
          inputProps={{
            inputMode: "decimal",
            readOnly,
            style: {
              textAlign: "right",
              cursor: readOnly ? "not-allowed" : "text",
            },
          }}
          sx={compactOutlinedSx(amountChanged)}
        />

        <Box
          onClick={() => setExpanded((o) => !o)}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 28,
            height: 28,
            borderRadius: "8px",
            cursor: "pointer",
            color: "#6B7280",
            "&:hover": { backgroundColor: "#F3F4F6" },
            justifySelf: "center",
          }}
        >
          {expanded ? (
            <KeyboardArrowUpIcon sx={{ fontSize: 20 }} />
          ) : (
            <KeyboardArrowDownIcon sx={{ fontSize: 20 }} />
          )}
        </Box>
      </Box>

      {/* Mobile layout — row1: checkbox + name + accordion (justify-between),
          row2: due date + amount side-by-side. */}
      <Box
        sx={{
          display: { xs: "flex", md: "none" },
          flexDirection: "column",
          gap: 1.25,
          padding: "12px 14px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, flex: 1, minWidth: 0 }}>
            <Box
              sx={{
                width: 18,
                height: 18,
                borderRadius: "4px",
                border: "1.5px solid #4D6BF6",
                backgroundColor: "#4D6BF6",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <CheckIcon sx={{ fontSize: 14, color: "#fff" }} />
            </Box>
            <TextField
              placeholder="Change Request Milestone"
              value={safeEdit.name ?? ""}
              onChange={(e) => {
                if (readOnly) return;
                onUpdate({ name: e.target.value });
              }}
              inputProps={{
                readOnly,
                style: {
                  cursor: readOnly ? "not-allowed" : "text",
                  fontWeight: 600,
                },
              }}
              sx={{
                ...compactOutlinedSx(false),
                flex: 1,
                minWidth: 0,
              }}
            />
          </Box>
          <Box
            onClick={() => setExpanded((o) => !o)}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 28,
              height: 28,
              borderRadius: "8px",
              cursor: "pointer",
              color: "#6B7280",
              flexShrink: 0,
              "&:hover": { backgroundColor: "#F3F4F6" },
            }}
          >
            {expanded ? (
              <KeyboardArrowUpIcon sx={{ fontSize: 20 }} />
            ) : (
              <KeyboardArrowDownIcon sx={{ fontSize: 20 }} />
            )}
          </Box>
        </Box>

        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.25 }}>
          <TextField
            type="date"
            value={isoDay(safeEdit.dueDate)}
            onChange={(e) => {
              if (readOnly) return;
              onUpdate({ dueDate: e.target.value });
            }}
            inputProps={{
              readOnly,
              style: { cursor: readOnly ? "not-allowed" : "text" },
            }}
            sx={compactOutlinedSx(!!safeEdit.dueDate)}
          />
          <TextField
            placeholder="Amount"
            value={formatAmountWithCommas(safeEdit.amount ?? "")}
            onChange={(e) => {
              if (readOnly) return;
              const stripped = e.target.value.replace(/,/g, "");
              if (
                stripped === "" ||
                stripped === "-" ||
                AMOUNT_REGEX.test(stripped)
              ) {
                onUpdate({ amount: stripped });
              }
            }}
            inputProps={{
              inputMode: "decimal",
              readOnly,
              style: {
                textAlign: "right",
                cursor: readOnly ? "not-allowed" : "text",
              },
            }}
            sx={compactOutlinedSx(amountChanged)}
          />
        </Box>
      </Box>

      {expanded && (
        <DeliverablesAccordion
          deliverables={safeEdit.deliverables || []}
          onUpdate={handleDeliverablesUpdate}
          readOnly={readOnly}
        />
      )}
    </Box>
  );
}

function MilestoneEditRow({
  milestone,
  edit,
  onUpdate,
  readOnly,
  selected,
  onToggleRow,
  onAutoSelect,
  gridCols,
}) {
  if (!edit) return null;

  const baseAmount = milestoneAmountOf(milestone);
  const baseDue = isoDay(milestoneDueDateOf(milestone));
  const amountChanged =
    String(edit.amount ?? "") !== String(baseAmount ?? "");
  const dueChanged = isoDay(edit.dueDate) !== baseDue;
  const editable = !readOnly;

  const expanded = !!edit.expanded;
  const setExpanded = (next) =>
    onUpdate({
      expanded: typeof next === "function" ? next(expanded) : next,
    });

  // Functional updater plus auto-select so the row's checkbox ticks
  // the moment the user touches the deliverables accordion.
  const handleDeliverablesUpdate = (updater) => {
    if (!selected) onAutoSelect?.();
    onUpdate((prev) => ({
      deliverables:
        typeof updater === "function"
          ? updater(prev?.deliverables || [])
          : updater,
    }));
  };

  const beforeEdit = () => {
    if (!selected) onAutoSelect?.();
  };

  return (
    <Box
      sx={{
        border: "1px solid #E5E7EB",
        borderRadius: "12px",
        background: selected ? "#F8FAFF" : "#fff",
        overflow: "hidden",
        transition: "background-color 0.15s",
      }}
    >
      {/* Desktop layout — single-row grid. */}
      <Box
        sx={{
          display: { xs: "none", md: "grid" },
          gridTemplateColumns: gridCols,
          gap: 2,
          alignItems: "center",
          padding: "10px 14px",
        }}
      >
        <Box
          onClick={readOnly ? undefined : onToggleRow}
          sx={{
            cursor: readOnly ? "not-allowed" : "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box
            sx={{
              width: 18,
              height: 18,
              borderRadius: "4px",
              border: `1.5px solid ${selected ? "#4D6BF6" : "#D7D8D9"}`,
              backgroundColor: selected ? "#4D6BF6" : "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "background-color 0.1s, border-color 0.1s",
              pointerEvents: readOnly ? "none" : "auto",
            }}
          >
            {selected && <CheckIcon sx={{ fontSize: 14, color: "#fff" }} />}
          </Box>
        </Box>

        <Typography
          onClick={readOnly ? undefined : onToggleRow}
          sx={{
            fontSize: 14,
            fontWeight: 500,
            color: "#0D0D0E",
            fontFamily: "Inter Tight",
            cursor: readOnly ? "not-allowed" : "pointer",
          }}
        >
          {milestoneLabelOf(milestone)}
        </Typography>

        <TextField
          type="date"
          value={isoDay(edit.dueDate)}
          onChange={(e) => {
            if (!editable) return;
            beforeEdit();
            onUpdate({ dueDate: e.target.value });
          }}
          inputProps={{
            readOnly: !editable,
            style: { cursor: editable ? "text" : "not-allowed" },
          }}
          sx={compactOutlinedSx(dueChanged)}
        />

        <TextField
          placeholder="Amount"
          value={formatAmountWithCommas(edit.amount ?? "")}
          onChange={(e) => {
            if (!editable) return;
            const stripped = e.target.value.replace(/,/g, "");
            if (
              stripped === "" ||
              stripped === "-" ||
              AMOUNT_REGEX.test(stripped)
            ) {
              beforeEdit();
              onUpdate({ amount: stripped });
            }
          }}
          inputProps={{
            inputMode: "decimal",
            readOnly: !editable,
            style: {
              textAlign: "right",
              cursor: editable ? "text" : "not-allowed",
            },
          }}
          sx={compactOutlinedSx(amountChanged)}
        />

        <Box
          onClick={() => setExpanded((o) => !o)}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 28,
            height: 28,
            borderRadius: "8px",
            cursor: "pointer",
            color: "#6B7280",
            "&:hover": { backgroundColor: "#F3F4F6" },
            justifySelf: "center",
          }}
        >
          {expanded ? (
            <KeyboardArrowUpIcon sx={{ fontSize: 20 }} />
          ) : (
            <KeyboardArrowDownIcon sx={{ fontSize: 20 }} />
          )}
        </Box>
      </Box>

      {/* Mobile layout — row1: checkbox + name + accordion (justify-between),
          row2: amount + due date side-by-side. */}
      <Box
        sx={{
          display: { xs: "flex", md: "none" },
          flexDirection: "column",
          gap: 1.25,
          padding: "12px 14px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Box
            onClick={readOnly ? undefined : onToggleRow}
            sx={{
              cursor: readOnly ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              gap: 1.25,
              flex: 1,
              minWidth: 0,
            }}
          >
            <Box
              sx={{
                width: 18,
                height: 18,
                borderRadius: "4px",
                border: `1.5px solid ${selected ? "#4D6BF6" : "#D7D8D9"}`,
                backgroundColor: selected ? "#4D6BF6" : "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "background-color 0.1s, border-color 0.1s",
                pointerEvents: readOnly ? "none" : "auto",
                flexShrink: 0,
              }}
            >
              {selected && <CheckIcon sx={{ fontSize: 14, color: "#fff" }} />}
            </Box>
            <Typography
              sx={{
                fontSize: 14,
                fontWeight: 500,
                color: "#0D0D0E",
                fontFamily: "Inter Tight",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {milestoneLabelOf(milestone)}
            </Typography>
          </Box>
          <Box
            onClick={() => setExpanded((o) => !o)}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 28,
              height: 28,
              borderRadius: "8px",
              cursor: "pointer",
              color: "#6B7280",
              flexShrink: 0,
              "&:hover": { backgroundColor: "#F3F4F6" },
            }}
          >
            {expanded ? (
              <KeyboardArrowUpIcon sx={{ fontSize: 20 }} />
            ) : (
              <KeyboardArrowDownIcon sx={{ fontSize: 20 }} />
            )}
          </Box>
        </Box>

        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.25 }}>
          <TextField
            type="date"
            value={isoDay(edit.dueDate)}
            onChange={(e) => {
              if (!editable) return;
              beforeEdit();
              onUpdate({ dueDate: e.target.value });
            }}
            inputProps={{
              readOnly: !editable,
              style: { cursor: editable ? "text" : "not-allowed" },
            }}
            sx={compactOutlinedSx(dueChanged)}
          />
          <TextField
            placeholder="Amount"
            value={formatAmountWithCommas(edit.amount ?? "")}
            onChange={(e) => {
              if (!editable) return;
              const stripped = e.target.value.replace(/,/g, "");
              if (
                stripped === "" ||
                stripped === "-" ||
                AMOUNT_REGEX.test(stripped)
              ) {
                beforeEdit();
                onUpdate({ amount: stripped });
              }
            }}
            inputProps={{
              inputMode: "decimal",
              readOnly: !editable,
              style: {
                textAlign: "right",
                cursor: editable ? "text" : "not-allowed",
              },
            }}
            sx={compactOutlinedSx(amountChanged)}
          />
        </Box>
      </Box>

      {expanded && (
        <DeliverablesAccordion
          deliverables={edit.deliverables || []}
          onUpdate={handleDeliverablesUpdate}
          readOnly={readOnly}
        />
      )}
    </Box>
  );
}

// Lifecycle bookkeeping. Both deliverables and sub-activities now
// carry status — ADDED stays ADDED, DELETED stays DELETED, anything
// else (null / MODIFIED) flips to MODIFIED on edit.
const bumpSubStatus = (s) => {
  if (s.status === DELIVERABLE_STATUS.DELETED) return s;
  if (s.status === DELIVERABLE_STATUS.ADDED) return s;
  return { ...s, status: DELIVERABLE_STATUS.MODIFIED };
};

const bumpDelStatus = (d) => {
  if (d.status === DELIVERABLE_STATUS.DELETED) return d;
  if (d.status === DELIVERABLE_STATUS.ADDED) return d;
  return { ...d, status: DELIVERABLE_STATUS.MODIFIED };
};

// Shared deliverables accordion. Wraps the RFC `DeliverableRow` so
// the look and behavior match the create-proposal flow. Deliverables
// and sub-activities follow the same lifecycle: ADDED on add, MODIFIED
// on edit, DELETED on soft-delete (rendered strikethrough). Only an
// abandoned brand-new add (empty save / cancel) is hard-removed.
function DeliverablesAccordion({ deliverables, onUpdate, readOnly }) {
  const addDeliverable = () =>
    onUpdate((prev) => [
      ...prev,
      {
        id: nextLocalId(),
        deliverable_description: "",
        start_date: "",
        end_date: "",
        sub_activities: [],
        status: DELIVERABLE_STATUS.ADDED,
        _new: true,
      },
    ]);

  const updateDeliverableName = (idx, text) =>
    onUpdate((prev) =>
      prev.map((d, i) =>
        i === idx
          ? bumpDelStatus({ ...d, deliverable_description: text })
          : d,
      ),
    );

  // Soft-delete by default (status → DELETED). Hard-remove only when
  // the user cancels / empty-saves a brand-new, never-committed add.
  const removeDeliverable = (idx, abandoned = false) =>
    onUpdate((prev) => {
      const target = prev[idx];
      if (!target) return prev;
      if (abandoned) return prev.filter((_, i) => i !== idx);
      return prev.map((d, i) =>
        i === idx ? { ...d, status: DELIVERABLE_STATUS.DELETED } : d,
      );
    });

  // Sub-activity edits — DELETED sub-activities now render inline at
  // their original position (strikethrough, inert), so the index from
  // DeliverableRow is already the full-array index.
  const addSubActivity = (delIdx) =>
    onUpdate((prev) =>
      prev.map((d, i) =>
        i === delIdx
          ? {
              ...d,
              sub_activities: [
                ...(d.sub_activities || []),
                {
                  id: nextLocalId(),
                  sub_activity_description: "",
                  status: DELIVERABLE_STATUS.ADDED,
                  _new: true,
                },
              ],
            }
          : d,
      ),
    );

  const updateSubActivityName = (delIdx, subIdx, text) =>
    onUpdate((prev) =>
      prev.map((d, i) =>
        i === delIdx
          ? {
              ...d,
              sub_activities: (d.sub_activities || []).map((s, j) =>
                j === subIdx
                  ? bumpSubStatus({ ...s, sub_activity_description: text })
                  : s,
              ),
            }
          : d,
      ),
    );

  // Remove a sub-activity. `abandoned` is true only when the user
  // cancels (or empty-saves) a brand-new sub that was never committed
  // — in that case we hard-remove it. Every other delete is a soft
  // delete (status → DELETED) so the payload retains the audit trail
  // and the row renders as a strikethrough.
  const removeSubActivity = (delIdx, subIdx, abandoned = false) =>
    onUpdate((prev) =>
      prev.map((d, i) => {
        if (i !== delIdx) return d;
        const subs = d.sub_activities || [];
        const target = subs[subIdx];
        if (!target) return d;
        if (abandoned) {
          return {
            ...d,
            sub_activities: subs.filter((_, j) => j !== subIdx),
          };
        }
        return {
          ...d,
          sub_activities: subs.map((s, j) =>
            j === subIdx ? { ...s, status: DELIVERABLE_STATUS.DELETED } : s,
          ),
        };
      }),
    );

  return (
    <Box
      sx={{
        padding: "10px 14px 14px",
        borderTop: "1px solid #F1F2F4",
        display: "flex",
        flexDirection: "column",
        gap: 1.25,
        backgroundColor: "#FAFAFB",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
          <Inventory2OutlinedIcon sx={{ fontSize: 18, color: "#6B7280" }} />
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 600,
              color: "#0D0D0E",
              fontFamily: "Inter Tight",
            }}
          >
            Deliverables
          </Typography>
          <Box
            sx={{
              padding: "1px 8px",
              borderRadius: "999px",
              backgroundColor: "#F3F4F6",
              color: "#6B7280",
              fontSize: 12,
              fontWeight: 500,
              fontFamily: "Inter Tight",
            }}
          >
            {
              (deliverables || []).filter(
                (d) => d.status !== DELIVERABLE_STATUS.DELETED,
              ).length
            }
          </Box>
        </Box>
        {!readOnly && (
          <button
            type="button"
            onClick={addDeliverable}
            style={{
              backgroundColor: "transparent",
              border: "1px dashed #4D6BF6",
              color: "#4D6BF6",
              borderRadius: "999px",
              padding: "6px 14px",
              fontSize: 12,
              fontWeight: 600,
              cursor: "pointer",
              fontFamily: "Inter Tight",
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              whiteSpace: "nowrap",
              boxShadow: "none",
            }}
          >
            <AddIcon sx={{ fontSize: 14 }} />
            Add deliverable
          </button>
        )}
      </Box>

      {(deliverables || []).length === 0 ? (
        <Typography
          sx={{
            fontSize: 13,
            color: "#9CA3AF",
            fontFamily: "Inter Tight",
            textAlign: "center",
            py: 1.5,
          }}
        >
          No deliverables yet. Click "Add deliverable" to create one.
        </Typography>
      ) : (
        (deliverables || []).map((d, idx) => (
          <DeliverableRow
            key={d.id || idx}
            deliverable={d}
            index={idx}
            onUpdateName={(text) => updateDeliverableName(idx, text)}
            onDelete={(abandoned) => removeDeliverable(idx, abandoned)}
            onAddSub={() => addSubActivity(idx)}
            onUpdateSubName={(sIdx, text) =>
              updateSubActivityName(idx, sIdx, text)
            }
            onDeleteSub={(sIdx, abandoned) =>
              removeSubActivity(idx, sIdx, abandoned)
            }
            disabled={readOnly}
            autoFocus={!!d._new}
            deleted={d.status === DELIVERABLE_STATUS.DELETED}
          />
        ))
      )}
    </Box>
  );
}

